import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import '../styles/Dashboard.css';
import '../styles/Header.css';
import { FiUser } from 'react-icons/fi';
import { toast } from 'react-hot-toast';

const Header = ({ 
  onAction, 
  onLogout, 
  onNavigateToHome, 
  onNavigateToOrcamentos,
  onNavigateToDiarioObra,
  showActionButtons = false, 
  isOffline = false, 
  saveStatus = 'initial', 
  hasUnsavedChanges = false 
}) => {
  const { currentUser, logout } = useAuth();
  const { darkMode, toggleTheme } = useTheme();
  const navigate = useNavigate();
  
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [currentPath, setCurrentPath] = useState(window.location.pathname);
  
  useEffect(() => {
    setCurrentPath(window.location.pathname);
  }, [navigate]);
  
  const isHomePage = currentPath === '/home';
  const isOrcamentosPage = currentPath === '/' || currentPath === '/orcamentos';
  const isDiarioObraPage = currentPath === '/diario-obra';
  const isDashboardPage = currentPath.startsWith('/orcamento/');
  const isControleCustosPage = currentPath === '/controle-custos';
  const isControleDiariasPage = currentPath === '/controle-diarias';
  
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showUserDropdown && !event.target.closest('.user-dropdown-container')) {
        setShowUserDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserDropdown]);

  const handleNavigateToDiarioObra = () => {
    setShowUserDropdown(false);
    if (onNavigateToDiarioObra) {
      onNavigateToDiarioObra();
    } else {
      navigate('/diario-obra');
    }
  };
  
  const handleNavigateToHome = () => {
    setShowUserDropdown(false);
    if (onNavigateToHome) {
      onNavigateToHome();
    } else {
      navigate('/home');
    }
  };
  
  const handleNavigateToOrcamentos = () => {
    setShowUserDropdown(false);
    if (onNavigateToOrcamentos) {
      onNavigateToOrcamentos();
    } else {
      navigate('/orcamentos');
    }
  };
  
  const handleNavigateToControleCustos = () => {
    setShowUserDropdown(false);
    navigate('/controle-custos');
  };
  
  const handleNavigateToControleDiarias = () => {
    setShowUserDropdown(false);
    navigate('/controle-diarias');
  };
  
  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      toast.error('Erro ao fazer logout. Tente novamente.');
    }
  };

  const handleSave = () => {
    onAction('save', 'Salvando dados...');
  };
  
  const handleGeneratePDF = () => {
    onAction('pdf', 'Gerando PDF. O download começará em instantes...');
  };
  
  const handleGenerateWord = () => {
    onAction('word', 'Gerando documento Word...');
  };

  
  let pageTitle = "ObrasWise";

  const canShowWordButton = currentUser?.showdoc === true;

  const getSaveIndicator = () => {
    if (saveStatus === 'unsaved') {
      return {
        text: 'Alterações não salvas',
        className: 'unsaved-changes-indicator',
        icon: 'fas fa-exclamation-circle'
      };
    } else if (saveStatus === 'saved') {
      return {
        text: 'Salvo',
        className: 'saved-indicator',
        icon: 'fas fa-check-circle'
      };
    } else {
      return {
        text: '',
        className: '',
        icon: ''
      };
    }
  };

  const saveIndicator = getSaveIndicator();

  return (
    <header className={`main-header ${darkMode ? 'dark-mode' : ''}`}>
      <div className="header-container">
        <div className="header-left">
          <h1 className="app-title" onClick={handleNavigateToHome} role="button">
            {pageTitle}
          </h1>
          
          {isOffline && (
            <div className="offline-indicator">
              <i className="fas fa-wifi-slash"></i> Offline
            </div>
          )}
          
          {saveIndicator.text && (
            <div className={saveIndicator.className}>
              <i className={saveIndicator.icon}></i>
              <span>{saveIndicator.text}</span>
            </div>
          )}
        </div>
        
        {showActionButtons && isDashboardPage && (
          <div className="header-actions">
            <button 
              className={`dashboard-action-button save-button ${saveStatus === 'unsaved' ? 'unsaved-changes' : ''}`} 
              onClick={handleSave}
              title="Salvar alterações"
            >
              <i className="fas fa-save"></i>
              <span>Salvar</span>
            </button>
            
            <button 
              className="dashboard-action-button" 
              onClick={handleGeneratePDF}
              title="Gerar arquivo PDF"
            >
              <i className="fas fa-file-pdf"></i>
              <span>PDF</span>
            </button>
            
            {canShowWordButton && (
              <button 
                className="dashboard-action-button" 
                onClick={handleGenerateWord}
                title="Gerar documento Word"
              >
                <i className="fas fa-file-word"></i>
                <span>Word</span>
              </button>
            )}
          </div>
        )}
        
        {showActionButtons && !isDashboardPage && (
          <div className="header-actions">
            {/* Removendo os botões "Novo Item" e "Exportar" */}
          </div>
        )}
        
        <div className="header-right">
          <button 
            className="theme-toggle-button" 
            onClick={toggleTheme}
            title={darkMode ? "Mudar para modo claro" : "Mudar para modo escuro"}
          >
            <i className={`fas ${darkMode ? 'fa-sun' : 'fa-moon'}`}></i>
          </button>
          
          <div className="user-info-display">
            <span className="user-name-display">{currentUser?.nome || currentUser?.email?.split('@')[0] || 'Usuário'}</span>
          </div>
          
          <div className="user-dropdown-container">
            <button 
              className="user-dropdown-button" 
              onClick={() => setShowUserDropdown(!showUserDropdown)}
            >
              {currentUser?.photoURL ? (
                <img 
                  src={currentUser.photoURL} 
                  alt={`Foto de ${currentUser?.nome || currentUser?.email}`}
                  className="user-photo"
                />
              ) : (
                <div className="user-photo-placeholder">
                  <i className="fas fa-user"></i>
                </div>
              )}
              <i className={`fas fa-chevron-down dropdown-arrow ${showUserDropdown ? 'open' : ''}`}></i>
            </button>
            
            {showUserDropdown && (
              <div className="user-dropdown-menu">
                <div className="dropdown-header">
                  <div className="dropdown-user-info">
                    <div className="dropdown-user-avatar">
                      {currentUser?.photoURL ? (
                        <img 
                          src={currentUser.photoURL} 
                          alt={`Foto de ${currentUser?.nome || currentUser?.email}`}
                          className="dropdown-user-photo"
                        />
                      ) : (
                        <div className="dropdown-user-photo-placeholder">
                          <i className="fas fa-user"></i>
                        </div>
                      )}
                    </div>
                    <div className="dropdown-user-details">
                      <div className="dropdown-user-name">{currentUser?.nome || currentUser?.email}</div>
                      <div className="dropdown-user-email">{currentUser?.email}</div>
                    </div>
                  </div>
                </div>
                <div className="dropdown-divider"></div>
                
                {!isHomePage && (
                  <div className="dropdown-item" onClick={handleNavigateToHome}>
                    <i className="fas fa-home"></i>
                    Página Inicial
                  </div>
                )}

                <div className="dropdown-item" onClick={() => {
                  setShowUserDropdown(false);
                  navigate('/configuracao-conta');
                }}>
                  <i className="fas fa-cog"></i>
                  Configuração de Conta
                </div>
                
                <div className="dropdown-item" onClick={() => {
                  setShowUserDropdown(false);
                  navigate('/cadastrar-cliente');
                }}>
                  <i className="fas fa-user-plus"></i>
                  Cadastrar Cliente
                </div>
                
                {!isOrcamentosPage && (
                  <div className="dropdown-item" onClick={handleNavigateToOrcamentos}>
                    <i className="fas fa-file-invoice-dollar"></i>
                    Orçamentos
                  </div>
                )}
                
                {!isDiarioObraPage && (
                  <div className="dropdown-item" onClick={handleNavigateToDiarioObra}>
                    <i className="fas fa-book"></i>
                    Diário de Obra
                  </div>
                )}
                
                {!isControleCustosPage && (
                  <div className="dropdown-item" onClick={handleNavigateToControleCustos}>
                    <i className="fas fa-chart-line"></i>
                    Controle de Custos
                  </div>
                )}
                
                {!isControleDiariasPage && (
                  <div className="dropdown-item" onClick={handleNavigateToControleDiarias}>
                    <i className="fas fa-chart-bar"></i>
                    Controle de Diárias
                  </div>
                )}
                
                <div className="dropdown-divider"></div>
                
                <div className="dropdown-item disabled">
                  <i className="fas fa-cog"></i>
                  Configurações <span className="coming-soon">em breve</span>
                </div>
                
                <div className="dropdown-divider"></div>
                <div className="dropdown-item" onClick={handleLogout}>
                  <i className="fas fa-sign-out-alt"></i>
                  Sair
                </div>

                <div className="dropdown-divider"></div>
                <Link to="/profile" className="dropdown-item">
                  <FiUser className="dropdown-icon" />
                  Meu Perfil
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header; 